import { SUDOKU_SHAPES, type SudokuSize } from '@hazel/shared'

/**
 * One grid, one table.
 *
 * The same rule Sky's Memory Match learned the hard way: the number of columns
 * the cursor steps by and the class that draws them live together, and a test
 * checks they still agree. Her board's cursor kept its own idea of the width —
 * four, always — while the CSS widened to six at a breakpoint, so Down moved
 * four cells across a six-wide row and looked like a broken cursor rather than
 * a mismatch.
 *
 * This grid is the easier case, and worth saying why: **a sudoku cannot
 * reflow.** Nine columns are nine columns on a phone, because eight columns of
 * a nine-wide puzzle is not the puzzle. So there is no breakpoint here at all,
 * and the column count is simply the size — which is exactly the assumption
 * that was wrong for Memory, and is right here for a reason rather than by
 * luck. The test says so, so that nobody later adds a breakpoint without
 * noticing what it costs.
 *
 * The class strings stay written out in full because Tailwind scans the source
 * for them; a template string would compile to nothing.
 */
export interface SudokuLayout {
  /** Columns on screen, at every width. Also what the cursor steps by. */
  readonly columns: number
  readonly rows: number
  /** How the boxes are cut, so the heavy rules land where the solver says. */
  readonly boxWidth: number
  readonly boxHeight: number
  readonly className: string
  /**
   * How big a value is drawn, per grid and per scale.
   *
   * Written down per size rather than left to inherit, because a square is a
   * ninth of the grid on the biggest one and a quarter on the smallest — so
   * one font size is either lost in a 4×4 square or spilling out of a 9×9 one.
   * A 9×9 digit at the body size was about fourteen pixels on a television,
   * which is a number nobody reads from a sofa.
   *
   * Not container-query units, which would do this with no table at all: this
   * repo has already been caught by `cq` units resolving to zero on a box
   * whose height comes from `flex-grow`, which is exactly how these squares
   * are sized.
   */
  readonly valueClassName: string
  /**
   * The same grid on the television, where the height is the scarce thing.
   *
   * Carries its own `data-scale=tv` prefixes rather than being prefixed where
   * it is used: half of one of Memory's reached the phone that way, and a
   * six-wide board on a 390px screen is a 46px card.
   *
   * Square, which makes this simpler than Memory's boards — both axes in `1fr`
   * and an aspect ratio of one. Stretched to the height it is given, it comes
   * out exactly as wide as it is tall: a block in the middle of the screen
   * rather than nine columns strewn across 1830px.
   */
  readonly tvClassName: string
}

export const SUDOKU_LAYOUT: Readonly<Record<SudokuSize, SudokuLayout>> = {
  4: {
    columns: 4,
    rows: 4,
    boxWidth: 2,
    boxHeight: 2,
    className: 'grid-cols-4',
    valueClassName: 'text-4xl [[data-scale=tv]_&]:text-7xl',
    tvClassName:
      '[[data-scale=tv]_&]:grid-cols-[repeat(4,minmax(0,1fr))] [[data-scale=tv]_&]:grid-rows-[repeat(4,minmax(0,1fr))] [[data-scale=tv]_&]:aspect-square',
  },
  6: {
    columns: 6,
    rows: 6,
    boxWidth: 3,
    boxHeight: 2,
    className: 'grid-cols-6',
    valueClassName: 'text-2xl [[data-scale=tv]_&]:text-5xl',
    tvClassName:
      '[[data-scale=tv]_&]:grid-cols-[repeat(6,minmax(0,1fr))] [[data-scale=tv]_&]:grid-rows-[repeat(6,minmax(0,1fr))] [[data-scale=tv]_&]:aspect-square',
  },
  9: {
    columns: 9,
    rows: 9,
    boxWidth: 3,
    boxHeight: 3,
    className: 'grid-cols-9',
    valueClassName: 'text-lg [[data-scale=tv]_&]:text-4xl',
    tvClassName:
      '[[data-scale=tv]_&]:grid-cols-[repeat(9,minmax(0,1fr))] [[data-scale=tv]_&]:grid-rows-[repeat(9,minmax(0,1fr))] [[data-scale=tv]_&]:aspect-square',
  },
}

/** How many columns are on screen for this grid, which is all of them. */
export function columnsFor(size: SudokuSize): number {
  return SUDOKU_LAYOUT[size].columns
}

/**
 * Where a cell lands after a step, or null if that would leave the grid.
 *
 * The cursor lives here rather than in the component, because "one cell in the
 * direction pressed" is the thing the ticket asks to be asserted and it should
 * be assertable without rendering anything.
 *
 * **Left and right do not run on to the next row.** Stepping right off the end
 * of a row and appearing at the start of the next one is what a text cursor
 * does, and a grid is not a line of text: she is reading across a row, and the
 * cursor jumping down a line loses her place. It stops instead.
 */
export function stepFrom(
  size: SudokuSize,
  cell: number,
  direction: 'left' | 'right' | 'up' | 'down',
): number | null {
  const { columns, rows } = SUDOKU_LAYOUT[size]
  const row = Math.floor(cell / columns)
  const column = cell % columns

  if (direction === 'left') return column > 0 ? cell - 1 : null
  if (direction === 'right') return column < columns - 1 ? cell + 1 : null
  if (direction === 'up') return row > 0 ? cell - columns : null

  return row < rows - 1 ? cell + columns : null
}

/**
 * The heavy rules between boxes, as classes for one cell.
 *
 * Drawn as thicker borders on the cells at a box's edge rather than as an
 * overlay, so they survive the grid being any size at all. Read from the same
 * shape the solver uses — if these disagreed, the grid she sees would be
 * divided differently from the grid the game is checking, which is a puzzle
 * that looks unsolvable and is not.
 */
export function boxEdges(size: SudokuSize, cell: number): string {
  const { columns, boxWidth, boxHeight } = SUDOKU_LAYOUT[size]
  const row = Math.floor(cell / columns)
  const column = cell % columns

  return [
    column % boxWidth === 0 && column > 0 ? 'border-l-ink border-l-2' : '',
    row % boxHeight === 0 && row > 0 ? 'border-t-ink border-t-2' : '',
  ]
    .filter(Boolean)
    .join(' ')
}

/**
 * That the drawn boxes are the solver's boxes.
 *
 * Exported for the test rather than used anywhere: a grid divided one way on
 * screen and another way in the rules is a puzzle that looks unsolvable and is
 * not, and she would have no way of telling which of the two was lying.
 */
export function boxShapeMatchesRules(size: SudokuSize): boolean {
  const layout = SUDOKU_LAYOUT[size]
  const rules = SUDOKU_SHAPES[size]

  return layout.boxWidth === rules.boxWidth && layout.boxHeight === rules.boxHeight
}

/** Everything about a square that decides how it is drawn. */
export interface CellMarking {
  readonly given: boolean
  readonly filled: boolean
  readonly conflicting: boolean
}

/**
 * How a square is marked, which is the whole of what this grid ever says.
 *
 * Two distinctions and no others.
 *
 * **Hers or the puzzle's.** Her own working is ringed so she can see at a
 * glance which squares she is allowed to change. With numbers the colour of
 * the digit says it too — but with teddies there is no digit to colour, and the
 * photograph fills the square and hides any background behind it, so a given
 * teddy and one she placed were identical. She would have found out which was
 * which by pressing a key and watching nothing happen.
 *
 * **Clashing.** Two of the same in one row, column or box — something she can
 * see for herself and check. Never "that differs from the answer", which would
 * be handing her the solution one square at a time.
 *
 * Drawn as an inset shadow rather than a border, so that switching one on
 * cannot shift the grid by a pixel, and so it composes with the cursor, which
 * owns the ring. Both are heavy on purpose: the first clash marking was
 * `bg-coral-soft`, which on the dark theme is #3d1f2a against a #15102a
 * surface — a difference nobody would notice on a television across a room,
 * for the only feedback this game gives.
 */
export function markingFor({ given, filled, conflicting }: CellMarking): string {
  if (conflicting) return 'bg-coral/25 text-coral shadow-[inset_0_0_0_3px_var(--color-coral)]'
  if (given || !filled) return ''

  return 'bg-primary/10 shadow-[inset_0_0_0_3px_var(--color-primary)]'
}

/**
 * The colour a number is drawn in, and always is.
 *
 * The same number is the same colour every time, which is the point of it: she
 * comes to know that 4 is green, and a row reads as a pattern rather than as
 * nine things to be compared one at a time. It is also what her card promises,
 * since the artwork draws a multicoloured grid.
 *
 * Written out one per line rather than built from a template, because Tailwind
 * scans the source for its class names and `text-digit-${n}` compiles to
 * nothing at all — the same reason the grid classes above are spelt out.
 *
 * A colour says which number this is and nothing else. It never means "right":
 * this grid says only whether two squares clash, and it says that in coral,
 * which is deliberately not one of these nine.
 */
const DIGIT_COLOURS = [
  'text-digit-1',
  'text-digit-2',
  'text-digit-3',
  'text-digit-4',
  'text-digit-5',
  'text-digit-6',
  'text-digit-7',
  'text-digit-8',
  'text-digit-9',
] as const

export function colourFor(value: number): string {
  return DIGIT_COLOURS[value - 1] ?? ''
}
